import React, { useState, useEffect } from "react";
import styles from "./searchPeople.module.css";
import PeopleSearchResults from "../peopleSearchResults/PeopleSearchResults";
import axios from "axios";
import { FaMagnifyingGlass } from "react-icons/fa6";

const apiUrl = process.env.REACT_APP_URL;

const SearchPeople = () => {
  /* The SearchPeople component allows the user to search for users on the website. It is used on the Search Page. The user can search for a user based on the requested type of cooperation - Language Exchange, and / or Learning a Language Together. The user should select the language he / she wants to learn. In the case of Language Exchange,  the user should also select the language he / she can offer help with. Another option is to search for a specific user, by entering a search query which will be compared against usernames, first names, and last names, in the database. Search results are displayed using the PeopleSearchResults component. */

  const [learnedLanguage, setLearnedLanguage] = useState("Italian");
  const [nativeLanguage, setNativeLanguage] = useState("English");

  const [isExchangeChecked, setIsExchangeChecked] = useState(true);
  const [isTogetherChecked, setIsTogetherChecked] = useState(true);
  const [isSpecificUserChecked, setIsSpecificUserChecked] = useState(false);

  const [searchedUser, setSearchedUser] = useState("");

  const [searchResults, setSearchResults] = useState([]);
  const [searchDone, setSearchDone] = useState(false);

  const [offset, setOffset] = useState(0);
  const [moreExchangeResults, setMoreExchangeResults] = useState(true);
  const [moreTogetherResults, setMoreTogetherResults] = useState(true);
  const [moreSpecificResults, setMoreSpecificResults] = useState(true);

  const [websiteLanguages, setWebsiteLanguages] = useState([]);

  /**
   * Get the list of supported languages from the database
   */
  useEffect(() => {
    const fetchData = async () => {
      const res = await axios.get(`${apiUrl}/search/languages`, {
        withCredentials: true,
        params: {},
      });

      if (res.data && res.data.languages) {
        const languagesArray = res.data.languages.map((lang) => lang.language);
        setWebsiteLanguages(languagesArray);
      } else {
        console.error("No languages data received from backend");
      }
    };
    fetchData();
  }, []);

  const languages = websiteLanguages.map((language) => (
    <option key={language}>{language}</option>
  ));

  /**
   * Handle the change of the learned language selection. The selected learned language cannot be identical to the selected native language, and in this case, the native language selection will be changed.
   */
  const handleLearnedLanguageChange = (event) => {
    const newLearnedLanguage = event.target.value;
    setLearnedLanguage(newLearnedLanguage);

    if (newLearnedLanguage === nativeLanguage) {
      if (newLearnedLanguage !== "English") {
        setNativeLanguage("English");
      } else {
        setNativeLanguage("Italian");
      }
    }

    setSearchDone(false);
  };

  /**
   * Handle checking / unchecking the Language Exchange checkbox
   */
  const handleExchangeCheckboxChange = (event) => {
    const checked = event.target.checked;
    setIsExchangeChecked(checked);
    setSearchDone(false);

    if (checked) {
      setIsSpecificUserChecked(false);
    } else if (!checked && !isTogetherChecked) {
      setIsSpecificUserChecked(true);
    }
  };

  /**
   * Handle checking / unchecking the Learn Together checkbox
   */
  const handleTogetherCheckboxChange = (event) => {
    const checked = event.target.checked;
    setIsTogetherChecked(checked);
    setSearchDone(false);

    if (checked) {
      setIsSpecificUserChecked(false);
    } else if (!checked && !isExchangeChecked) {
      setIsSpecificUserChecked(true);
    }
  };

  /**
   * Handle checking / unchecking the Specific User checkbox
   */
  const handleSpecificUserCheckboxChange = (event) => {
    const checked = event.target.checked;
    setIsSpecificUserChecked(checked);
    setSearchDone(false);

    if (checked) {
      setIsExchangeChecked(false);
      setIsTogetherChecked(false);
    } else {
      setIsExchangeChecked(true);
      setIsTogetherChecked(true);
    }
  };

  /**
   * Handle the change of the native language selection. The selected native language cannot be identical to the selected learned language, and in this case, the learned language selection will be changed.
   */
  const handleNativeLanguageChange = (event) => {
    const newNativeLanguage = event.target.value;
    setNativeLanguage(newNativeLanguage);

    if (newNativeLanguage === learnedLanguage) {
      if (newNativeLanguage !== "English") {
        setLearnedLanguage("English");
      } else {
        setLearnedLanguage("Italian");
      }
    }

    setSearchDone(false);
  };

  /**
   * Handle change of the search query for the specific user search.
   */
  const handleInputChange = (event) => {
    setSearchedUser(event.target.value);
    setSearchDone(false);
  };

  /**
   * Handle search form submission
   */
  const handleFormSubmit = (e) => {
    e.preventDefault();
    setMoreExchangeResults(true);
    setMoreTogetherResults(true);
    setMoreSpecificResults(true);
    getResults(0); // Start from offset 0 for a new search
  };

  /**
   * Show more search results
   */
  function handleViewMoreClick() {
    const newOffset = offset + 5;
    setOffset(newOffset);
    getResults(newOffset);
  }

  /**
   * Search for a specific user, using the current searchedUser value.
   * @param {*} currentOffset - the number of search results to skip
   */
  const searchForUser = async (currentOffset) => {
    const res = await axios.get(`${apiUrl}/search/users/specific`, {
      withCredentials: true,
      params: {
        search_query: searchedUser,
        qty: 5,
        offset: currentOffset,
      },
    });

    if (res.data) {
      if (res.data.users.length < 5) {
        setMoreSpecificResults(false);
      }

      if (currentOffset === 0) {
        setSearchResults(res.data.users);
      } else {
        setSearchResults([...searchResults, ...res.data.users]);
      }

      setSearchDone(true);
    }
  };

  /**
   * Search the database according to the selected parameters and entered search query.
   * @param {*} currentOffset - the number of search results to skip
   */
  const getResults = async (currentOffset) => {
    try {
      let newResults = [];

      if (isSpecificUserChecked) {
        await searchForUser(currentOffset);
        return;
      }

      if (isExchangeChecked) {
        const resExchange = await axios.get(`${apiUrl}/search/users/exchange`, {
          withCredentials: true,
          params: {
            native_lang: nativeLanguage,
            learned_lang: learnedLanguage,
            qty: 5,
            offset: currentOffset,
          },
        });

        if (resExchange.data) {
          if (resExchange.data.users.length < 5) {
            setMoreExchangeResults(false);
          }

          newResults = resExchange.data.users;
        }
      }

      if (isTogetherChecked) {
        const resTogether = await axios.get(`${apiUrl}/search/users/together`, {
          withCredentials: true,
          params: {
            learned_lang: learnedLanguage,
            qty: 5,
            offset: currentOffset,
          },
        });

        if (resTogether.data) {
          if (resTogether.data.users.length < 5) {
            setMoreTogetherResults(false);
          }

          newResults = [...newResults, ...resTogether.data.users];
        }
      }

      if (currentOffset === 0) {
        setSearchResults(newResults);
      } else {
        setSearchResults([...searchResults, ...newResults]);
      }

      setSearchDone(true);
    } catch (error) {
      console.error("Error submitting form", error);
    }
  };

  return (
    <div className={styles.searchPeople}>
      <form className={styles.searchPeopleForm} onSubmit={handleFormSubmit}>
        <div className={styles.formGroup + " " + styles.learnedLangGroup}>
          <label className={styles.titleStyle} htmlFor="searchLearnedLang">
            I want to learn:{" "}
          </label>
          <select
            id="searchLearnedLang"
            className={styles.learnedLanguageSelect}
            value={learnedLanguage}
            onChange={handleLearnedLanguageChange}
          >
            {languages}
          </select>
        </div>

        <p className={styles.titleStyle}>I'm looking for:</p>

        <div className={styles.formGroup}>
          <input
            type="checkbox"
            checked={isExchangeChecked}
            onChange={handleExchangeCheckboxChange}
          />
          <label className={styles.optionInputStyle}>Language Exchange</label>
        </div>

        {isExchangeChecked && (
          <div className={styles.formGroup}>
            <label className={styles.titleStyle}>I can help with: </label>

            <select
              id="searchLearnedLang"
              className={styles.nativeLanguageSelect}
              value={nativeLanguage}
              onChange={handleNativeLanguageChange}
            >
              {languages}
            </select>
          </div>
        )}

        <div className={styles.formGroup}>
          <input
            type="checkbox"
            checked={isTogetherChecked}
            onChange={handleTogetherCheckboxChange}
          />
          <label className={styles.optionInputStyle}>Learn Together</label>
        </div>

        <p className={styles.categorySeperator}>OR:</p>
        <div className={styles.formGroup}>
          <input
            type="checkbox"
            checked={isSpecificUserChecked}
            onChange={handleSpecificUserCheckboxChange}
          />
          <label className={styles.titleStyle}>A specific user</label>
        </div>

        {isSpecificUserChecked && (
          <div className={styles.formGroup + " " + styles.usernameGroup}>
            <label className={styles.optionInputStyle}>
              User Details (username / full name):
            </label>
            <input
              type="text"
              placeholder="JaneDoe21"
              value={searchedUser}
              onChange={handleInputChange}
            />
          </div>
        )}

        <button>
          Search{" "}
          <span className={styles.searchBtnIcon}>
            <FaMagnifyingGlass />
          </span>
        </button>
      </form>

      {searchDone && (
        <PeopleSearchResults
          searchResults={searchResults}
          learnedLanguage={learnedLanguage}
          offeredLanguage={nativeLanguage}
        />
      )}

      {searchDone &&
        ((isExchangeChecked && moreExchangeResults) ||
          (isTogetherChecked && moreTogetherResults) ||
          (isSpecificUserChecked && moreSpecificResults)) && (
          <button className={styles.viewMoreBtn} onClick={handleViewMoreClick}>
            View More
          </button>
        )}
    </div>
  );
};

export default SearchPeople;
